import React, { useState, useCallback } from "react";
import {
  Paper,
  Stack,
  Typography,
  Box,
  LinearProgress,
  Chip,
  Button,
  Skeleton,
  alpha,
  CircularProgress,
} from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import InfoButton from "../Clinical/InfoButton";
import {
  getHedisTarget,
  getMeasureStatus,
  formatNumber,
  WORKFLOW_CHIP_COLORS,
} from "../../constants/ceoDashboard";

/**
 * Status chip color configuration for quality measure compliance.
 */
const STATUS_CONFIG = {
  on_track: { bgcolor: "#D1FAE5", color: "#065F46", label: "On Track" },
  at_risk: { bgcolor: "#FEF3C7", color: "#92400E", label: "At Risk" },
  behind: { bgcolor: "#FEE2E2", color: "#991B1B", label: "Behind" },
};

/**
 * Progress bar color by status.
 */
const BAR_COLORS = {
  on_track: "#10B981",
  at_risk: "#F59E0B",
  behind: "#EF4444",
};

/** Max workflow chips visible before collapsing */
const MAX_WORKFLOW_CHIPS = 5;

/**
 * Renders a single progress bar row (Availity or Sante+).
 */
function RateBar({ label, rate, target, barColor, opacity = 1 }) {
  const status = getMeasureStatus(rate, target);
  const chipCfg = STATUS_CONFIG[status];

  return (
    <Stack spacing={0.3}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{ minWidth: 52, color: "text.secondary" }}
        >
          {label}
        </Typography>

        <Box sx={{ flex: 1, position: "relative" }}>
          <LinearProgress
            variant="determinate"
            value={Math.min(rate, 100)}
            sx={{
              height: 10,
              borderRadius: 5,
              bgcolor: (theme) => alpha(theme.palette.divider, 0.3),
              "& .MuiLinearProgress-bar": {
                borderRadius: 5,
                bgcolor: alpha(barColor, opacity),
              },
            }}
          />
          {/* Target marker */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: `${Math.min(target, 100)}%`,
              transform: "translateX(-50%)",
              width: 2,
              height: 10,
              bgcolor: "text.primary",
              opacity: 0.6,
              borderRadius: 1,
            }}
          />
        </Box>

        <Typography
          variant="caption"
          fontWeight={600}
          sx={{ minWidth: 40, textAlign: "right" }}
        >
          {rate}%
        </Typography>

        <Chip
          label={chipCfg.label}
          size="small"
          sx={{
            height: 20,
            fontSize: "0.65rem",
            fontWeight: 600,
            bgcolor: chipCfg.bgcolor,
            color: chipCfg.color,
          }}
        />
      </Stack>
    </Stack>
  );
}

/**
 * Renders workflow status chips for a measure.
 */
function WorkflowChips({ workflowStatuses, expanded, onToggle }) {
  if (!workflowStatuses) return null;

  const entries = Object.entries(workflowStatuses)
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a);

  if (entries.length === 0) return null;

  const visible = expanded ? entries : entries.slice(0, MAX_WORKFLOW_CHIPS);
  const hiddenCount = entries.length - MAX_WORKFLOW_CHIPS;

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
      {visible.map(([statusName, count]) => {
        const chipColor = WORKFLOW_CHIP_COLORS[statusName] || {
          bg: "#F3F4F6",
          text: "#374151",
        };
        return (
          <Chip
            key={statusName}
            label={`${statusName}: ${count}`}
            size="small"
            sx={{
              height: 20,
              fontSize: "0.6rem",
              fontWeight: 600,
              bgcolor: chipColor.bg,
              color: chipColor.text,
            }}
          />
        );
      })}
      {!expanded && hiddenCount > 0 && (
        <Chip
          label={`+${hiddenCount} more`}
          size="small"
          onClick={onToggle}
          sx={{
            height: 20,
            fontSize: "0.6rem",
            fontWeight: 600,
            bgcolor: "#E5E7EB",
            color: "#374151",
            cursor: "pointer",
            "&:hover": { bgcolor: "#D1D5DB" },
          }}
        />
      )}
      {expanded && hiddenCount > 0 && (
        <Chip
          label="Show less"
          size="small"
          onClick={onToggle}
          sx={{
            height: 20,
            fontSize: "0.6rem",
            fontWeight: 600,
            bgcolor: "#E5E7EB",
            color: "#374151",
            cursor: "pointer",
            "&:hover": { bgcolor: "#D1D5DB" },
          }}
        />
      )}
    </Stack>
  );
}

/**
 * Panel 3 — Quality Measures (HEDIS) with dual progress bars (Availity + Sante+).
 * Muestra compliance general, totales de gaps, barras de progreso duales por medida,
 * y workflow status chips.
 *
 * @param {{ data: Object, loading: boolean, helpContent: Object, loadAllMeasures: Function }} props
 */
export default function PanelQualityMeasures({
  data,
  loading,
  helpContent,
  loadAllMeasures,
}) {
  const [showAll, setShowAll] = useState(false);
  const [allMeasures, setAllMeasures] = useState(null);
  const [loadingAll, setLoadingAll] = useState(false);
  const [expandedWorkflows, setExpandedWorkflows] = useState(new Set());

  // ── Handlers ───────────────────────────────────────────────────
  const handleToggleShowAll = useCallback(async () => {
    if (!showAll && allMeasures === null && loadAllMeasures) {
      setLoadingAll(true);
      try {
        const result = await loadAllMeasures();
        setAllMeasures(result?.measures ?? []);
      } catch {
        // Si falla, mantenemos el top measures
        setAllMeasures(null);
        setLoadingAll(false);
        return;
      }
      setLoadingAll(false);
    }
    setShowAll((prev) => !prev);
  }, [showAll, allMeasures, loadAllMeasures]);

  const handleToggleWorkflow = useCallback((measureName) => {
    setExpandedWorkflows((prev) => {
      const next = new Set(prev);
      if (next.has(measureName)) {
        next.delete(measureName);
      } else {
        next.add(measureName);
      }
      return next;
    });
  }, []);

  // ── Loading state ──────────────────────────────────────────────
  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack spacing={2}>
          <Skeleton variant="text" width="50%" animation="wave" />
          <Skeleton variant="text" width="70%" animation="wave" />
          <Skeleton
            variant="rectangular"
            height={24}
            sx={{ borderRadius: 1 }}
            animation="wave"
          />
          <Skeleton
            variant="rectangular"
            height={24}
            sx={{ borderRadius: 1 }}
            animation="wave"
          />
          <Skeleton
            variant="rectangular"
            height={24}
            sx={{ borderRadius: 1 }}
            animation="wave"
          />
        </Stack>
      </Paper>
    );
  }

  if (!data) return null;

  const { summary: rawSummary, topMeasures: rawTopMeasures } = data;
  const summary = rawSummary || {};
  const topMeasures = rawTopMeasures || {};
  const {
    overallCompliance = 0,
    totalGaps = 0,
    closedGaps = 0,
    openGaps = 0,
    uniqueMeasures = 0,
  } = summary;

  const topList = topMeasures.measures ?? [];
  const visibleMeasures = showAll && allMeasures ? allMeasures : topList;

  const overallStatus = getMeasureStatus(overallCompliance, 75);
  const overallStatusCfg = STATUS_CONFIG[overallStatus];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack spacing={2.5}>
        {/* ── Header ──────────────────────────────────────────── */}
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: 2,
              background:
                "linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)",
              color: "white",
              boxShadow: `0 4px 12px ${alpha("#8B5CF6", 0.35)}`,
            }}
          >
            <AssessmentIcon fontSize="small" />
          </Box>
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
            Quality Measures (HEDIS)
          </Typography>
          <InfoButton helpContent={helpContent} />
        </Stack>

        {/* ── Overall stats row ───────────────────────────────── */}
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          sx={{ rowGap: 1 }}
        >
          <Typography
            variant="body1"
            fontWeight={700}
            sx={{ color: overallStatusCfg.color }}
          >
            Overall Compliance: {overallCompliance}%
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatNumber(totalGaps)} total gaps
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatNumber(closedGaps)} closed / {formatNumber(openGaps)} open
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {uniqueMeasures} measures
          </Typography>
        </Stack>

        {/* ── Measure bars ────────────────────────────────────── */}
        <Stack spacing={2.5}>
          {visibleMeasures.map((measure) => {
            const target = getHedisTarget(measure.measureName);
            const avalityStatus = getMeasureStatus(
              measure.avalityRate ?? 0,
              target
            );
            const spStatus = getMeasureStatus(measure.spRate ?? 0, target);
            const avalityBarColor = BAR_COLORS[avalityStatus];
            const spBarColor = BAR_COLORS[spStatus];

            return (
              <Box key={measure.measureName}>
                {/* Measure name + target */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 0.5 }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    noWrap
                    sx={{ flex: 1, mr: 1 }}
                  >
                    {measure.measureName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Target: {target}%
                  </Typography>
                </Stack>

                {/* Dual bars */}
                <Stack spacing={0.5}>
                  <RateBar
                    label="Availity"
                    rate={measure.avalityRate ?? 0}
                    target={target}
                    barColor={avalityBarColor}
                  />
                  <RateBar
                    label="Sante+"
                    rate={measure.spRate ?? 0}
                    target={target}
                    barColor={spBarColor}
                    opacity={0.7}
                  />
                </Stack>

                {/* Workflow status chips */}
                {measure.workflowStatuses && (
                  <Box sx={{ mt: 0.75 }}>
                    <WorkflowChips
                      workflowStatuses={measure.workflowStatuses}
                      expanded={expandedWorkflows.has(measure.measureName)}
                      onToggle={() =>
                        handleToggleWorkflow(measure.measureName)
                      }
                    />
                  </Box>
                )}

                {/* Bottom stats: closed/total | SP: spClosed/total */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mt: 0.5 }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {formatNumber(measure.closed)} /{" "}
                    {formatNumber(measure.total)} | SP:{" "}
                    {formatNumber(measure.spClosed)} /{" "}
                    {formatNumber(measure.total)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Open: {formatNumber(measure.open)} | Submitted:{" "}
                    {formatNumber(measure.submitted)}
                  </Typography>
                </Stack>
              </Box>
            );
          })}
        </Stack>

        {/* ── Toggle button ───────────────────────────────────── */}
        <Button
          size="small"
          onClick={handleToggleShowAll}
          disabled={loadingAll}
          endIcon={
            loadingAll ? (
              <CircularProgress size={14} />
            ) : showAll ? (
              <ExpandLessIcon />
            ) : (
              <ExpandMoreIcon />
            )
          }
          sx={{
            alignSelf: "center",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
          }}
        >
          {loadingAll
            ? "Loading measures..."
            : showAll
              ? `Show top ${topList.length}`
              : `Show all ${uniqueMeasures} measures`}
        </Button>
      </Stack>
    </Paper>
  );
}
